import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState } from 'react';
import ActorModelInfographic from '../components/architecture/ActorModelInfographic';
import EventSourcingInfographic from '../components/architecture/EventSourcingInfographic';
import SagaPatternInfographic from '../components/architecture/SagaPatternInfographic';
import WebSocketsInfographic from '../components/architecture/WebSocketsInfographic';
import PubSubInfographic from '../components/architecture/PubSubInfographic';
import ProblemSection from '../components/architecture/ProblemSection';
import ComparisonSection from '../components/architecture/ComparisonSection';
import OperationalGuarantees from '../components/architecture/OperationalGuarantees';
import Icon from '../components/Icon';

type TabId = 'actors' | 'events' | 'saga' | 'realtime' | 'pubsub';

interface Tab {
  id: TabId;
  label: string;
  icon: string;
  tagline: string;
}

const tabs: Tab[] = [
  { id: 'actors', label: 'Actor Model', icon: 'smart_toy', tagline: 'One assistant per auction' },
  { id: 'events', label: 'Event Sourcing', icon: 'history', tagline: 'The auction diary' },
  { id: 'saga', label: 'Saga Pattern', icon: 'sync', tagline: 'Multi-step transactions' },
  { id: 'realtime', label: 'WebSockets', icon: 'bolt', tagline: 'Instant updates' },
  { id: 'pubsub', label: 'Pub/Sub Lite', icon: 'cell_tower', tagline: 'Push, don\'t poll' },
];

function CodeBlock({ code, title }: { code: string; title: string }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="mt-6 border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between text-left"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Icon name="code" size="small" />
          {title}
        </span>
        <Icon name={isOpen ? 'expand_more' : 'chevron_right'} size="small" className="text-gray-400" />
      </button>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <pre className="bg-gray-900 text-gray-100 p-4 overflow-x-auto text-sm font-mono">
            <code>{code}</code>
          </pre>
        </motion.div>
      )}
    </div>
  );
}

function ActorModelSection() {
  return (
    <div className="space-y-6">
      <ActorModelInfographic />
      
      <CodeBlock
        title="Want to see the code? (Click to expand)"
        code={`export class AuctionActor extends DurableObject<Env> {
  private state: AuctionState | null = null;

  async fetch(request: Request): Promise<Response> {
    // All requests processed ONE AT A TIME
    // No race conditions possible!
    const url = new URL(request.url);
    
    switch (url.pathname) {
      case '/bid':
        return this.handleBid(request);
      case '/state':
        return this.handleGetState();
      default:
        return new Response('Not found', { status: 404 });
    }
  }

  private async handleBid(request: Request) {
    const { userId, amount } = await request.json();
    
    // Because we process one message at a time,
    // this check is always accurate
    if (amount <= this.state.currentBid) {
      return Response.json({ error: 'Bid too low' });
    }
    
    // Safe to update - no other request can interfere
    this.state.currentBid = amount;
    this.state.highestBidder = userId;
    
    return Response.json({ success: true });
  }
}`}
      />
    </div>
  );
}

function EventSourcingSection() {
  return (
    <div className="space-y-6">
      <EventSourcingInfographic />
      
      <CodeBlock
        title="Want to see the code? (Click to expand)"
        code={`// Every change is an event we append to the diary
async appendEvent(event: AuctionEvent) {
  await this.sql.exec(
    \`INSERT INTO events (type, data, timestamp) 
     VALUES (?, ?, ?)\`,
    event.type,
    JSON.stringify(event.data),
    Date.now()
  );
  // Events are NEVER deleted or modified
  // This is our permanent audit trail
}

// To get current state, we replay the diary
async rehydrate(): Promise<AuctionState> {
  const events = await this.sql.exec(
    'SELECT * FROM events ORDER BY timestamp ASC'
  ).toArray();

  // Start fresh and apply each event
  let state = { currentBid: 0, bids: [] };
  
  for (const event of events) {
    switch (event.type) {
      case 'BID_PLACED':
        state.currentBid = event.data.amount;
        state.bids.push(event.data);
        break;
      // ... handle other event types
    }
  }
  
  return state;
}`}
      />
    </div>
  );
}

function SagaPatternSection() {
  return (
    <div className="space-y-6">
      <SagaPatternInfographic />
      
      <CodeBlock
        title="Want to see the code? (Click to expand)"
        code={`// The Saga Coordinator manages the whole flow
class PaymentSagaActor extends DurableObject {
  
  async executeBidSaga(userId: string, amount: number) {
    // Step 1: Reserve funds in wallet
    const reserved = await this.reserveFunds(userId, amount);
    if (!reserved.success) {
      return { error: 'Insufficient funds' };
    }
    
    // Step 2: Place the bid
    const bidPlaced = await this.placeBid(userId, amount);
    if (!bidPlaced.success) {
      // COMPENSATION: Undo step 1!
      await this.releaseFunds(userId, amount);
      return { error: 'Bid rejected' };
    }
    
    // Step 3: Confirm the hold
    await this.confirmHold(userId, amount);
    
    return { success: true };
  }
  
  // If we crash mid-saga, we resume from where we left off
  // State is persisted after each step
}`}
      />
    </div>
  );
}

function RealtimeSection() {
  return (
    <div className="space-y-6">
      <WebSocketsInfographic />
      
      <CodeBlock
        title="Want to see the code? (Click to expand)"
        code={`// Accept a WebSocket connection with hibernation
async fetch(request: Request) {
  if (request.headers.get('Upgrade') === 'websocket') {
    const pair = new WebSocketPair();
    const [client, server] = Object.values(pair);
    
    // Accept with hibernation enabled
    // Actor can sleep, connection stays open!
    this.ctx.acceptWebSocket(server);
    
    return new Response(null, { 
      status: 101, 
      webSocket: client 
    });
  }
}

// Actor wakes up ONLY when a message arrives
async webSocketMessage(ws: WebSocket, message: string) {
  const data = JSON.parse(message);
  
  if (data.type === 'bid') {
    await this.handleBid(data);
    
    // Tell EVERYONE watching about the new bid
    this.broadcast({ 
      type: 'bid_update', 
      amount: this.state.currentBid 
    });
  }
}

// Send to all connected browsers
private broadcast(message: object) {
  for (const ws of this.ctx.getWebSockets()) {
    ws.send(JSON.stringify(message));
  }
}`}
      />
    </div>
  );
}

function PubSubSection() {
  return (
    <div className="space-y-6">
      <PubSubInfographic />
      
      <CodeBlock
        title="Want to see the code? (Click to expand)"
        code={`// In AuctionActor: Push events to all subscribers
private async pushToSubscribers(event: AuctionEvent) {
  const subscribers = await this.loadSubscribers();
  
  // Send to each subscriber (bots, dashboards, etc.)
  for (const sub of subscribers) {
    const botActor = this.env.BOT_ACTOR.get(
      this.env.BOT_ACTOR.idFromString(sub.id)
    );
    
    // Push the event - no polling needed!
    await botActor.fetch(new Request('http://internal/event', {
      method: 'POST',
      body: JSON.stringify({ auctionId: this.id, event })
    }));
  }
}

// In BotActor: React to events instantly
async handleEvent(auctionId: string, event: AuctionEvent) {
  // We just got notified - no waiting!
  if (event.type === 'bid' && this.shouldCounterBid(event)) {
    // Respond with a counter-bid
    await this.placeBid(auctionId, event.amount + 5);
  }
}`}
      />
    </div>
  );
}

export default function ArchitecturePage() {
  const [activeTab, setActiveTab] = useState<TabId>('actors');

  const renderContent = () => {
    switch (activeTab) {
      case 'actors':
        return <ActorModelSection />;
      case 'events':
        return <EventSourcingSection />;
      case 'saga':
        return <SagaPatternSection />;
      case 'realtime':
        return <RealtimeSection />;
      case 'pubsub':
        return <PubSubSection />;
    }
  };

  const activeTabData = tabs.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-900 font-semibold">
            <Icon name="sell" className="text-xl text-primary-600" />
            Auction Platform
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/how-it-works" className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
              <Icon name="lightbulb" size="small" variant="outlined" />
              Simple Version
            </Link>
            <Link to="/demo" className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
              <Icon name="play_circle" size="small" variant="outlined" />
              Try Demo
            </Link>
            <a
              href="https://github.com/josejalvarezm/auction-actors-platform-private"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-1"
            >
              GitHub
              <Icon name="open_in_new" size="small" />
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-24 pb-8 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block bg-purple-100 text-purple-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
              Technical Deep Dive
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How It Really Works
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-2">
              The patterns that make auctions fast, fair, and reliable.
            </p>
            <p className="text-sm text-gray-500">
              Don't worry, we explain everything from scratch!
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Problem Section */}
      <ProblemSection />

      {/* Tab Navigation */}
      <section className="px-4 mb-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap justify-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
                }`}
              >
                <Icon name={tab.icon} size="medium" />
                <div className="text-left">
                  <div className="text-sm font-semibold">{tab.label}</div>
                  {activeTab === tab.id && (
                    <div className="text-xs opacity-80">{tab.tagline}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Section Header */}
      <section className="px-4 mb-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <Icon name={activeTabData?.icon || 'info'} className="text-4xl text-purple-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{activeTabData?.label}</h2>
              <p className="text-gray-500">{activeTabData?.tagline}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderContent()}
          </motion.div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-8 px-4 bg-gray-50 border-t border-gray-100">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 text-center">
            Jump to another pattern
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            {tabs.filter(t => t.id !== activeTab).map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors text-sm"
              >
                <Icon name={tab.icon} size="small" />
                <span className="text-gray-700">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Why Not...? Comparison Section */}
      <ComparisonSection />

      {/* Operational Guarantees */}
      <OperationalGuarantees />

      {/* Blog Link */}
      <section className="py-12 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Want the Full Story?
          </h2>
          <p className="text-gray-400 mb-6">
            Our 9-part blog series covers every decision, trade-off, and lesson learned.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://github.com/josejalvarezm/auction-actors-platform-private/tree/main/blog"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-gray-900 font-semibold px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Icon name="menu_book" />
              Read the Blog Series
            </a>
            <Link
              to="/demo"
              className="inline-flex items-center gap-2 bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Icon name="sports_esports" />
              Try the Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-gray-900 border-t border-gray-800 text-gray-400">
        <div className="max-w-4xl mx-auto text-center text-sm">
          <p className="mb-3">
            Built with Cloudflare Durable Objects, Workers, and D1.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/how-it-works" className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
              <Icon name="arrow_back" size="small" />
              Simpler explanation
            </Link>
            <Link to="/" className="text-gray-400 hover:text-gray-300 flex items-center gap-1">
              <Icon name="home" size="small" />
              Home
            </Link>
            <Link to="/demo" className="text-green-400 hover:text-green-300 flex items-center gap-1">
              Try the demo
              <Icon name="arrow_forward" size="small" />
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
